/**
 * The menu frame every board menu shares (spec M1-B2 §8.1, M1-B3 §8.3).
 *
 * Measured `div.ds-menu-inner`, **264 px** wide (`[ui:library/table-14-group-header-menu.json]`,
 * 264 x 549 at (386, 253)) for the group and board menus and **220 px** for the row menus
 * (`ul.menu_b3b6d18d5a.medium_5f70d516fb`, 220 x 420 / 220 x 452
 * `[ui:library/item-02-context-menu.json]`), Figtree 15 px / 22.5 px, `rgb(50,51,56)`.
 *
 * `role="menu"` with arrow-key navigation, `Escape` to close and focus restoration (A1, A4).
 */
import { isUsableAnchor, placeAnchored, useOverlayDismiss } from '@elizmonday/ui';
import { useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react';
import { createPortal } from 'react-dom';

export interface MenuEntry {
  readonly id: string;
  readonly label: string;
  readonly disabled?: boolean;
  /**
   * `▸`. A parent with entries opens a **flyout** to its right on click (monday opens it on
   * hover; click is the accessible-by-default reading and Escape closes it, ASSUMED). A
   * parent with an *empty* array is a slot whose contents were not captured, and renders as
   * a disabled parent.
   */
  readonly submenu?: readonly MenuEntry[];
  /**
   * The flyout's own width. monday sizes a submenu independently of its parent — the item
   * menu is 220 and its `Move to ▸` is **240** (`[c:item-menu]`, `[c:item-menu-move-to]`) —
   * and inheriting the parent's width was a measured 20 px diff.
   */
  readonly submenuWidth?: number;
  readonly divider?: boolean;
  readonly danger?: boolean;
  /**
   * Why the entry is disabled, shown as monday shows it — a native `title` tooltip. B10: a
   * greyed entry with no reason is the one thing a migrated user cannot interpret.
   */
  readonly title?: string;
  /** Rendered right-aligned: monday's keyboard hint, or a `✓` on a toggle entry. */
  readonly hint?: string;
  readonly 'data-testid'?: string;
  onSelect?(): void;
}

export function Menu({
  entries,
  x,
  y,
  width = 264,
  label,
  onClose,
  onDismiss,
  children,
  nested = false,
}: {
  entries?: readonly MenuEntry[];
  x: number;
  y: number;
  width?: number;
  label: string;
  onClose(): void;
  /**
   * **P-260.** What Escape and an outside click mean, when that is not what selecting an entry
   * means. A **flyout** passes `() => setFlyout(null)`: dismissing a submenu closes the submenu
   * and leaves its parent open, so focus returns to the row that opened it — a row that is still
   * in the document. Escape used to run the parent's `onClose` as well, unmounting that row
   * inside its own focus restoration, which is how the sweep found focus on `<body>`.
   */
  onDismiss?(): void;
  children?: ReactNode;
  /**
   * **P-053.** A flyout is rendered *inside* its parent's node and opens **no portal of its
   * own**, so the parent's `node.contains(event.target)` dismiss test covers the whole menu
   * tree. It used to portal to `document.body` like the root, which made every submenu a
   * *sibling* of its parent: a `mousedown` on a submenu row was "outside" the parent, the
   * capture-phase listener closed the root menu, and React unmounted the row before its own
   * `click` could fire. Seven of monday's item and group actions were unreachable by mouse.
   *
   * `position: fixed` is what makes nesting free — a fixed box is positioned against the
   * viewport and is not clipped by the parent's `overflow: auto`, so the flyout still lands
   * on the coordinates the parent row measured.
   */
  nested?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [flyout, setFlyout] = useState<{ id: string; x: number; y: number } | null>(null);
  /**
   * **P-260.** The control the menu was opened from, and the return of focus to it on close —
   * the one mechanism in `packages/ui`, no longer a copy of it here. It is called before the
   * placement effect below because that effect reads the opener in the same commit (P-007: a
   * caller with no coordinate anchors the menu to its trigger), and a layout effect declared
   * earlier runs earlier.
   *
   * `captureEscape` keeps the menu's historical capture-phase listener; the hook's overlay stack
   * is what makes a flyout's Escape close the flyout rather than the whole tree.
   */
  const opener = useOverlayDismiss({
    onClose: () => (onDismiss ?? onClose)(),
    captureEscape: true,
  });
  /**
   * P-005. Two defects, both found by hit-testing on a phone:
   *
   *   1. the menu rendered **inline**, so one opened from inside the mobile sidebar drawer
   *      joined the drawer's stacking context and `z-index: 500` meant nothing;
   *   2. nothing clamped it, so a 240 px menu opened at x 350 on a 390 px viewport ran off
   *      the right edge — `onScreen: false`, half of every entry unreachable.
   *
   * It is a body portal now, and it is clamped into the viewport with an 8 px margin.
   */
  const [placed, setPlaced] = useState<{ left: number; top: number }>({ left: x, top: y });

  const [maxWidth, setMaxWidth] = useState<number | null>(null);

  useLayoutEffect(() => {
    const clamp = () => {
      const box = ref.current?.getBoundingClientRect();
      const next = placeAnchored(
        anchorRectFor(x, y, opener.current),
        { width: box?.width ?? width, height: box?.height ?? 0 },
        { width: window.innerWidth, height: window.innerHeight },
        // A menu opens **at** the point it was given (gap 0) and pins to the viewport rather
        // than flipping above it: both are the measured desktop behaviour, kept byte-identical.
        { gap: 0, flip: false, margin: MENU_MARGIN },
      );
      setPlaced((current) =>
        current.left === next.left && current.top === next.top ? current : { left: next.left, top: next.top },
      );
      setMaxWidth((current) => (current === next.maxWidth ? current : next.maxWidth));
    };
    clamp();
    window.addEventListener('resize', clamp);
    // The opener may live in a horizontal scroller (the phone toolbar, the grid's summary row);
    // `true` so a scroll inside it re-places the menu instead of leaving it behind.
    window.addEventListener('scroll', clamp, true);
    return () => {
      window.removeEventListener('resize', clamp);
      window.removeEventListener('scroll', clamp, true);
    };
  }, [x, y, width, entries, children]);

  useEffect(() => {
    const node = ref.current;
    node?.querySelector<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')?.focus();

    const onKeyDown = (event: KeyboardEvent) => {
      if (!node) return;
      // Escape (and the focus return that follows it) belongs to `useOverlayDismiss` above.
      if (event.key !== 'ArrowDown' && event.key !== 'ArrowUp') return;
      event.preventDefault();
      const items = [...node.querySelectorAll<HTMLElement>('[role="menuitem"]:not([aria-disabled="true"])')];
      const at = items.indexOf(document.activeElement as HTMLElement);
      const next = event.key === 'ArrowDown' ? at + 1 : at - 1;
      items[(next + items.length) % items.length]?.focus();
    };
    const onPointerDown = (event: MouseEvent) => {
      if (node && !node.contains(event.target as Node)) (onDismiss ?? onClose)();
    };
    document.addEventListener('keydown', onKeyDown, true);
    /*
     * P-053: only the **root** of a menu tree watches for the click that dismisses it. A
     * flyout lives inside that root's node, so the root's own `contains` test already keeps
     * a click on a submenu row inside the menu; a second listener on the flyout would read
     * every click on its *parent* as "outside" and close the whole tree from underneath it.
     */
    if (!nested) document.addEventListener('mousedown', onPointerDown, true);
    return () => {
      document.removeEventListener('keydown', onKeyDown, true);
      document.removeEventListener('mousedown', onPointerDown, true);
    };
  }, [onClose, onDismiss, nested]);

  const menu = (
    <div
      ref={ref}
      className="ez-menu"
      role="menu"
      aria-label={label}
      data-testid={`menu-${label.toLowerCase().replaceAll(' ', '-')}`}
      style={{ left: placed.left, top: placed.top, width, ...(maxWidth === null ? undefined : { maxWidth }) }}
    >
      {children}
      {entries?.map((entry, index) =>
        entry.divider ? (
          <hr key={`d${index}`} className="ez-menu__divider" />
        ) : (
          <button
            key={entry.id}
            type="button"
            role="menuitem"
            className="ez-menu__item"
            data-danger={entry.danger || undefined}
            data-testid={entry['data-testid']}
            title={entry.title}
            aria-disabled={entry.disabled || undefined}
            aria-haspopup={entry.submenu ? 'menu' : undefined}
            disabled={entry.disabled}
            aria-expanded={entry.submenu && entry.submenu.length > 0 ? flyout?.id === entry.id : undefined}
            onClick={(event) => {
              entry.onSelect?.();
              if (!entry.submenu) {
                onClose();
                return;
              }
              if (entry.submenu.length === 0) return;
              const box = event.currentTarget.getBoundingClientRect();
              setFlyout((current) =>
                current?.id === entry.id ? null : { id: entry.id, x: box.right - 4, y: box.top },
              );
            }}
          >
            <span>{entry.label}</span>
            {entry.hint ? <span className="ez-menu__hint">{entry.hint}</span> : null}
            {entry.submenu ? <span aria-hidden="true">▸</span> : null}
          </button>
        ),
      )}

      {flyout
        ? (() => {
            const parent = entries?.find((entry) => entry.id === flyout.id);
            if (!parent?.submenu || parent.submenu.length === 0) return null;
            return (
              <Menu
                nested
                entries={parent.submenu}
                x={flyout.x}
                y={flyout.y}
                width={parent.submenuWidth ?? width}
                label={parent.label}
                onClose={() => {
                  setFlyout(null);
                  onClose();
                }}
                onDismiss={() => setFlyout(null)}
              />
            );
          })()
        : null}
    </div>
  );

  // A submenu renders inside its parent's node; only the root menu opens a portal (P-053).
  return typeof document === 'undefined' || nested ? menu : createPortal(menu, document.body);
}

/** The viewport margin a clamped menu keeps, matching `AnchoredPopover`. */
const MENU_MARGIN = 8;

/**
 * Where the menu is anchored (parity card **P-007**).
 *
 * Almost every caller hands a **point** — the bottom-left corner of the control, or the cursor
 * for a context menu — and that point is used as it is. Two callers cannot: the add-column
 * gallery and `Add column to the right` open the same menu from places that carry no
 * coordinate, and passing `(0, 0)` used to clamp to `(8, 8)` — the top-left corner of the
 * window, over the sidebar, while the `+` sat at x ≈ 1,420. That was the whole of P-007.
 *
 * So `(0, 0)` is read as *"no coordinate"* and the menu anchors to the control that opened it —
 * the element that had focus when the menu mounted. With neither a point nor an opener it still
 * clamps to the margin, which is the old behaviour and the only case left where it can happen.
 */
function anchorRectFor(x: number, y: number, opener: Element | null) {
  if (x !== 0 || y !== 0) return { x, y, width: 0, height: 0 };
  const box = opener?.getBoundingClientRect();
  return isUsableAnchor(box) ? { x: box.x, y: box.bottom, width: box.width, height: 0 } : { x, y, width: 0, height: 0 };
}
